import React, { useCallback, useMemo, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import { AutoAwesome as AiIcon, InfoOutlined as InfoOutlinedIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useGetAttraction,
  useGetAttractionImages,
  useGetAttractionOffers,
  useResetAttractionImage,
  useUpdateAttractionImage,
} from '~/hooks/apis/attractions';

import { formatAttractionType } from '~/types/attractionTypes';

import ProximityMap from './ProximityMap';

const DEFAULT_PAGE_SIZE = 10;

const MATCH_TYPE_LABELS: Record<string, string> = {
  DIRECT_TITLE: 'Direct Title',
  AI_SEMANTIC: 'AI Semantic',
  PROXIMITY: 'Proximity',
  KEYWORD: 'Keyword',
};

const MATCH_TYPE_COLORS: Record<string, 'default' | 'success' | 'info' | 'warning' | 'primary'> = {
  DIRECT_TITLE: 'success',
  AI_SEMANTIC: 'primary',
  PROXIMITY: 'info',
  KEYWORD: 'warning',
};

export default function AttractionDetailPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const history = useHistory();
  const { attractionId } = useParams<{ attractionId: string }>();

  const [offersPage, setOffersPage] = useState(1);
  const [offersPageSize, setOffersPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  const { data: attraction, isLoading: isAttractionLoading } = useGetAttraction(attractionId);

  const offersParams = useMemo(
    () => ({
      limit: offersPageSize,
      offset: (offersPage - 1) * offersPageSize,
    }),
    [offersPage, offersPageSize],
  );

  const {
    data: offersData,
    isLoading: isOffersLoading,
    isFetching: isOffersFetching,
    error: offersError,
  } = useGetAttractionOffers(attractionId, offersParams);
  const offers = offersData?.result ?? [];
  const offersTotal = offersData?.total ?? 0;

  const {
    data: imagesData,
    isLoading: isImagesLoading,
    error: imagesError,
  } = useGetAttractionImages(attractionId, {
    enabled: isImagePickerOpen,
  });
  const images = imagesData?.result ?? [];

  const setImageMutation = useUpdateAttractionImage();
  const resetImageMutation = useResetAttractionImage();

  const offersPageCount = Math.ceil(offersTotal / offersPageSize);

  const handleOffersPageChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
    setOffersPage(value);
  }, []);

  const handleOffersPageSizeChange = useCallback((event: SelectChangeEvent<number>) => {
    setOffersPageSize(Number(event.target.value));
    setOffersPage(1);
  }, []);

  const handleSetImage = useCallback(
    (imageId: string, imageUrl: string) => {
      setImageMutation.mutate({ attractionId, imageId, imageUrl }, { onSuccess: () => setIsImagePickerOpen(false) });
    },
    [attractionId, setImageMutation],
  );

  const handleResetImage = useCallback(() => {
    resetImageMutation.mutate(attractionId, {
      onSuccess: () => setIsImagePickerOpen(false),
    });
  }, [attractionId, resetImageMutation]);

  const OFFER_COLUMNS = ['Title', 'Provider', 'Match Type', 'Relevance'];

  if (isAttractionLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!attraction) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Attraction Not Found" backButton />
        <Alert severity="error">Attraction not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={attraction.name}
        subtitle={`${attraction.city}, ${attraction.country} (${attraction.countryCode})`}
        backButton
        sticky
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={formatAttractionType(attraction.type)} size="small" />
          <Chip
            label={attraction.active ? 'Active' : 'Inactive'}
            size="small"
            color={attraction.active ? 'success' : 'default'}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </PageHeader>

      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {attraction.description || 'No description'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', height: 320 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRight: '1px solid',
              borderColor: 'divider',
              minWidth: 100,
            }}
          >
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                #{attraction.popularityRank}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', mt: 0.25 }}
              >
                Rank
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                {attraction.offerCount}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', mt: 0.25 }}
              >
                Links
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <ProximityMap
              latitude={attraction.latitude}
              longitude={attraction.longitude}
              name={attraction.name}
              radiusMeters={5000}
              offers={offers}
              totalOffers={offersTotal}
              onOfferClick={(offerId) => history.push(`/experience/${offerId}`)}
            />
          </Box>

          <Box
            sx={{
              width: 360,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                Hero Image
              </Typography>
              <Button
                size="small"
                variant="text"
                sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}
                onClick={() => setIsImagePickerOpen(true)}
              >
                Change
              </Button>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
              {attraction.imageUrl ? (
                <Box
                  component="img"
                  src={
                    attraction.imageUrl.startsWith('http')
                      ? attraction.imageUrl
                      : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_400,h_300/${attraction.imageUrl}`
                  }
                  alt={attraction.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: attraction.imageUrl.includes('wikimedia') ? 'contain' : 'cover',
                    backgroundColor: attraction.imageUrl.includes('wikimedia') ? '#f5f5f5' : undefined,
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    No image set
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: alpha(theme.palette.action.hover, 0.02),
          }}
        >
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            ID:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {attraction.attractionId}
            </Box>
          </Typography>
          {attraction.lePlaceId && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              Place:{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {attraction.lePlaceId}
              </Box>
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AiIcon sx={{ fontSize: 12, color: 'primary.main' }} />
            <Typography variant="caption" color="primary" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
              AI Generated
            </Typography>
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ position: 'relative' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Linked Offers ({offersTotal})
          </Typography>
        </Box>
        {offersError && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
            Error loading offers: {offersError instanceof Error ? offersError.message : String(offersError)}
          </Alert>
        )}
        {isOffersFetching && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, height: 2 }} />
        )}
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-body': { fontSize: '0.8125rem', verticalAlign: 'middle' } }}>
            <TableHead>
              <TableRow>
                {OFFER_COLUMNS.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      padding: 1,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'white',
                      whiteSpace: 'nowrap',
                      backgroundColor: theme.palette.primary.dark,
                      textAlign: col !== 'Title' ? 'center' : 'left',
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isOffersLoading && offers.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {OFFER_COLUMNS.map((col) => (
                        <TableCell key={col}>
                          <Skeleton variant="rounded" height={20} sx={{ minWidth: 40 }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : offers.map((offer) => {
                    const relevancePct = Math.round((offer.relevanceScore ?? 0) * 100);
                    const relevanceColor =
                      relevancePct >= 80 ? 'success.main' : relevancePct >= 50 ? 'warning.main' : 'error.main';
                    return (
                      <TableRow
                        key={offer.id}
                        sx={{
                          cursor: 'pointer',
                          '&:last-child td': { borderBottom: 0 },
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                          transition: 'background-color 0.15s ease',
                        }}
                        onClick={() => history.push(`/experience/${offer.id}`)}
                      >
                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.82rem',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {offer.title}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center' }}>
                          <Chip
                            label={offer.provider}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center' }}>
                          <Chip
                            label={MATCH_TYPE_LABELS[offer.matchType] ?? offer.matchType}
                            size="small"
                            color={MATCH_TYPE_COLORS[offer.matchType] ?? 'default'}
                            sx={{ fontSize: '0.65rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 1.5, minWidth: 100 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                flex: 1,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[300], 0.5),
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${relevancePct}%`,
                                  borderRadius: 2,
                                  bgcolor: relevanceColor,
                                  transition: 'width 0.3s',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                minWidth: 28,
                                textAlign: 'right',
                                color: relevanceColor,
                              }}
                            >
                              {relevancePct}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Pagination
            count={offersPageCount}
            page={offersPage}
            onChange={handleOffersPageChange}
            color="primary"
            size="small"
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {offersTotal > 0 && (
              <Typography variant="caption" color="text.disabled">
                {offersTotal} {offersTotal === 1 ? 'offer' : 'offers'}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Per page:
              </Typography>
              <Select
                size="small"
                value={offersPageSize}
                onChange={handleOffersPageSizeChange}
                sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
              >
                {[10, 25, 50].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={isImagePickerOpen} onClose={() => setIsImagePickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Hero Image</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.04),
              border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.2 }} />
            <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5 }}>
              Images are filtered by <strong>AI Vision</strong> to show only those depicting this attraction. When no
              offer image matches, a <strong>Wikipedia Commons</strong> image is used as reference. The order reflects
              relevance to the POI. Selecting an image sets it as the hero across search results and detail pages.
            </Box>
          </Box>
          {imagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading images: {imagesError instanceof Error ? imagesError.message : String(imagesError)}
            </Alert>
          )}
          {isImagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No images available from linked offers.
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {images.map((image) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.imageId}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': { borderColor: themeColor },
                      position: 'relative',
                    }}
                    onClick={() => handleSetImage(image.imageId, image.url)}
                  >
                    <CardMedia
                      component="img"
                      image={image.url}
                      alt={image.offerTitle}
                      sx={{
                        height: 120,
                        objectFit: image.url.includes('wikimedia') ? 'contain' : 'cover',
                        backgroundColor: image.url.includes('wikimedia') ? '#f5f5f5' : undefined,
                      }}
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {image.offerTitle}
                      </Typography>
                    </Box>
                    {setImageMutation.isPending && setImageMutation.variables?.imageId === image.imageId && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip title="Reset to auto-selected image">
            <Button startIcon={<RefreshIcon />} onClick={handleResetImage} disabled={resetImageMutation.isPending}>
              Reset to Auto
            </Button>
          </Tooltip>
          <Button onClick={() => setIsImagePickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
